import { functionType } from '@/composables/useWorkflowAssistantFunctions'

const refName = (ref) => typeof ref === 'string'
  ? ref.split('/').pop().replace(/([a-z])([A-Z])/g, '$1 $2')
  : ''

const quote = (value) => value ? ` "${value}"` : ''

const count = (list, singular, plural) => {
  const size = Array.isArray(list) ? list.length : 0

  return `${size} ${size === 1 ? singular : plural}`
}

// An explicit `type` wins: a conversion still carries the old shape in its ref.
const shapeOf = (ref, type) => {
  if (type === 'decision' || type === 'domainEvent') {
    return type === 'decision' ? 'decision' : 'event'
  }

  return typeof ref === 'string' && ref.includes('/decisions/') ? 'decision' : 'event'
}

// Last resort: a tool without a label must still read as English, never as raw snake_case.
const humanize = (name) => {
  const words = String(name)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim()

  return words.charAt(0).toUpperCase() + words.slice(1)
}

const labels = {
  [functionType.UPDATE_WORKFLOW_NAME]: (args) => `Renaming the workflow to${quote(args.name)}`,
  [functionType.CREATE_EVENT]: (args) => `Adding ${shapeOf(null, args.type)}${quote(args.description)}`,
  [functionType.UPDATE_EVENT]: (args) => `Updating ${shapeOf(args.domainEvent, args.type)}${quote(args.description || refName(args.domainEvent))}`,
  [functionType.DELETE_EVENT]: (args) => `Deleting ${shapeOf(args.domainEvent)}${quote(refName(args.domainEvent))}`,
  [functionType.ADD_CONNECTION]: (args) => `Connecting${quote(refName(args.from))} to${quote(refName(args.to))}`,
  [functionType.REMOVE_CONNECTION]: (args) => `Disconnecting${quote(refName(args.from))} from${quote(refName(args.to))}`,
  [functionType.CREATE_LANE]: (args) => `Adding lane${quote(args.name)}`,
  [functionType.UPDATE_LANE]: (args) => `Updating lane${quote(args.lane)}`,
  [functionType.DELETE_LANE]: (args) => `Deleting lane${quote(args.lane)}`,
  [functionType.CREATE_GROUP]: (args) => `Adding group${quote(args.name)}`,
  [functionType.UPDATE_GROUP]: (args) => `Updating group${quote(args.group)}`,
  [functionType.DELETE_GROUP]: (args) => `Deleting group${quote(args.group)}`,
  [functionType.CREATE_CARD]: (args) => `Adding a card to${quote(refName(args.domainEvent))}`,
  [functionType.UPDATE_CARD]: (args) => `Updating a card on${quote(refName(args.domainEvent))}`,
  [functionType.DELETE_CARD]: (args) => `Deleting a card from${quote(refName(args.domainEvent))}`,
  [functionType.DETACH_SCHEMAS]: (args) => `Detaching ${count(args.detachments, 'schema', 'schemas')}`,
  [functionType.CREATE_ENTITY]: (args) => `Creating ${count(args.entities, 'entity', 'entities')}`,
  [functionType.UPDATE_ENTITY]: (args) => `Updating ${count(args.entities, 'entity', 'entities')}`,
  [functionType.DELETE_ENTITY]: (args) => `Deleting entity${quote(refName(args.entity))}`,
  [functionType.CREATE_COMMAND]: (args) => `Creating ${count(args.commands, 'command', 'commands')}`,
  [functionType.UPDATE_COMMAND]: (args) => `Updating ${count(args.commands, 'command', 'commands')}`,
  [functionType.DELETE_COMMAND]: (args) => `Deleting command${quote(refName(args.command))}`,
  [functionType.CREATE_READ_MODEL]: (args) => `Creating ${count(args.readModels, 'read model', 'read models')}`,
  [functionType.UPDATE_READ_MODEL]: (args) => `Updating ${count(args.readModels, 'read model', 'read models')}`,
  [functionType.DELETE_READ_MODEL]: (args) => `Deleting read model${quote(refName(args.readModel))}`,
  // "schema" is not jargon here: a workflow has both diagram domain events and their schemas.
  [functionType.CREATE_DOMAIN_EVENT_SCHEMA]: (args) => `Creating ${count(args.domainEventSchemas, 'domain event schema', 'domain event schemas')}`,
  [functionType.UPDATE_DOMAIN_EVENT_SCHEMA]: (args) => `Updating ${count(args.domainEventSchemas, 'domain event schema', 'domain event schemas')}`,
  [functionType.DELETE_DOMAIN_EVENT_SCHEMA]: (args) => `Deleting domain event schema${quote(refName(args.domainEventSchema))}`,
  [functionType.CREATE_BOUNDED_CONTEXT]: (args) => `Adding bounded context${quote(args.name)}`,
  [functionType.UPDATE_BOUNDED_CONTEXT]: (args) => `Updating bounded context${quote(args.boundedContext)}`,
  [functionType.DELETE_BOUNDED_CONTEXT]: (args) => `Deleting bounded context${quote(args.boundedContext)}`,
  [functionType.REGENERATE_CARDS]: () => 'Regenerating cards',
  [functionType.ADD_MISSING_CARDS]: () => 'Adding missing cards',
  [functionType.REGENERATE_COMMANDS]: () => 'Regenerating commands',
  [functionType.ADD_MISSING_COMMANDS]: () => 'Adding missing commands',
  [functionType.REGENERATE_READ_MODELS]: () => 'Regenerating read models',
  [functionType.ADD_MISSING_READ_MODELS]: () => 'Adding missing read models',
  [functionType.REGENERATE_ENTITIES]: () => 'Regenerating entities',
  [functionType.ADD_MISSING_ENTITIES]: () => 'Adding missing entities',
  [functionType.GENERATE_ENTITIES_FROM_DESCRIPTION]: () => 'Generating entities',
  [functionType.GENERATE_COMMANDS_FROM_DESCRIPTION]: () => 'Generating commands',
  [functionType.GENERATE_READ_MODELS_FROM_DESCRIPTION]: () => 'Generating read models',
  [functionType.GENERATE_CARDS_FROM_DESCRIPTION]: () => 'Generating cards',
  [functionType.PLAN_WORKFLOW_WITH_AI]: () => 'Planning the workflow',
  [functionType.VALIDATE_DOMAIN_MODEL]: () => 'Checking the domain model',
  [functionType.ANALYZE_DOMAIN_MODEL]: () => 'Analysing the domain model',
  [functionType.ROLLBACK_WORKFLOW]: () => 'Undoing recent changes'
}

// Present-tense description shown while the call runs, e.g. `Adding event "Ticket Reviewed"`.
export const getActionLabel = (name, args) => {
  const build = labels[name]

  // `args` can be an explicit null, which a default parameter would not catch.
  return build ? build(args || {}) : humanize(name)
}
